import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2 } from 'lucide-react';
import { NumericInput } from '../components/NumericInput';
import SectionBackButton from '../components/SectionBackButton';
import { DEFAULT_WORKOUT_WHEEL_CONFIG } from '../data/workoutWheelDefaults';
import { normalizeWorkoutWheelConfig } from '../lib/workoutWheelConfig';
import { fetchWorkoutWheelConfig, upsertWorkoutWheelConfig } from '../lib/workoutWheelApi';
import type { WorkoutWheelConfig, WorkoutWheelExerciseRow, WorkoutWheelTier } from '../types';

function cloneConfig(c: WorkoutWheelConfig): WorkoutWheelConfig {
  return {
    ...c,
    green: c.green.map((r) => ({ ...r })),
    yellow: c.yellow.map((r) => ({ ...r })),
    red: c.red.map((r) => ({ ...r })),
  };
}

function newRow(): WorkoutWheelExerciseRow {
  return {
    id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `row-${Date.now()}`,
    name: '',
    prescription: '',
  };
}

const TIER_META: { tier: WorkoutWheelTier; title: string }[] = [
  { tier: 'green', title: 'Green (Easy)' },
  { tier: 'yellow', title: 'Yellow (Medium)' },
  { tier: 'red', title: 'Red (Hard)' },
];

const WorkoutWheelSettings: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<WorkoutWheelConfig>(() => normalizeWorkoutWheelConfig(null));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const saved = await fetchWorkoutWheelConfig();
        if (cancelled) return;
        setForm(saved ? cloneConfig(saved) : cloneConfig(normalizeWorkoutWheelConfig(null)));
      } catch (e) {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : 'Load failed');
        setForm(cloneConfig(normalizeWorkoutWheelConfig(null)));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const updateRow = (tier: WorkoutWheelTier, index: number, patch: Partial<WorkoutWheelExerciseRow>) => {
    setForm((f) => {
      const key = tier as keyof Pick<WorkoutWheelConfig, 'green' | 'yellow' | 'red'>;
      const rows = [...f[key]];
      rows[index] = { ...rows[index], ...patch };
      return { ...f, [key]: rows };
    });
  };

  const removeRow = (tier: WorkoutWheelTier, index: number) => {
    setForm((f) => {
      const key = tier as keyof Pick<WorkoutWheelConfig, 'green' | 'yellow' | 'red'>;
      return { ...f, [key]: f[key].filter((_, i) => i !== index) };
    });
  };

  const addRow = (tier: WorkoutWheelTier) => {
    setForm((f) => {
      const key = tier as keyof Pick<WorkoutWheelConfig, 'green' | 'yellow' | 'red'>;
      return { ...f, [key]: [...f[key], newRow()] };
    });
  };

  const onSave = async () => {
    const trimmed = normalizeWorkoutWheelConfig({
      ...form,
      green: form.green.filter((r) => r.name.trim() && r.prescription.trim()),
      yellow: form.yellow.filter((r) => r.name.trim() && r.prescription.trim()),
      red: form.red.filter((r) => r.name.trim() && r.prescription.trim()),
    });
    if (trimmed.green.length === 0 || trimmed.yellow.length === 0 || trimmed.red.length === 0) {
      setError('Each tier needs at least one exercise with name and prescription.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await upsertWorkoutWheelConfig(trimmed);
      navigate('/gym/workout-wheel');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <>
        <SectionBackButton to="/gym/workout-wheel" label="Back to wheel" />
        <div className="min-h-screen flex items-center justify-center bg-theme-secondary text-theme-primary">
          Loading…
        </div>
      </>
    );
  }

  return (
    <>
      <SectionBackButton to="/gym/workout-wheel" label="Back to wheel" />
      <div className="min-h-screen bg-theme-secondary pb-24 px-4 pt-4">
        <div className="max-w-xl mx-auto space-y-6">
          <h1 className="text-2xl font-bold text-theme-primary">Workout wheel settings</h1>
          <p className="text-theme-secondary text-sm">
            Saved to your account (syncs across devices). Built-in lists are the default until you save.
          </p>

          {error && (
            <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 px-3 py-2 text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <label className="block col-span-2 sm:col-span-1">
              <span className="text-sm text-theme-secondary">Spins per round</span>
              <NumericInput
                value={form.spinCount}
                min={1}
                max={20}
                className="mt-1 w-full rounded border border-theme-primary bg-theme-primary text-theme-primary px-2 py-1"
                onCommit={(spinCount) => setForm((f) => ({ ...f, spinCount }))}
              />
            </label>
            <label className="block col-span-2 sm:col-span-1">
              <span className="text-sm text-theme-secondary">Green copies / exercise</span>
              <NumericInput
                value={form.copiesGreen}
                min={1}
                max={10}
                className="mt-1 w-full rounded border border-theme-primary bg-theme-primary text-theme-primary px-2 py-1"
                onCommit={(copiesGreen) => setForm((f) => ({ ...f, copiesGreen }))}
              />
            </label>
            <label className="block">
              <span className="text-sm text-theme-secondary">Yellow copies / exercise</span>
              <NumericInput
                value={form.copiesYellow}
                min={1}
                max={10}
                className="mt-1 w-full rounded border border-theme-primary bg-theme-primary text-theme-primary px-2 py-1"
                onCommit={(copiesYellow) => setForm((f) => ({ ...f, copiesYellow }))}
              />
            </label>
            <label className="block">
              <span className="text-sm text-theme-secondary">Red copies / exercise</span>
              <NumericInput
                value={form.copiesRed}
                min={1}
                max={10}
                className="mt-1 w-full rounded border border-theme-primary bg-theme-primary text-theme-primary px-2 py-1"
                onCommit={(copiesRed) => setForm((f) => ({ ...f, copiesRed }))}
              />
            </label>
          </div>

          {TIER_META.map(({ tier, title }) => (
            <section key={tier} className="rounded-lg border border-theme-primary bg-theme-primary p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <h2 className="font-semibold text-theme-primary">{title}</h2>
                <button
                  type="button"
                  onClick={() => addRow(tier)}
                  className="inline-flex items-center gap-1 text-sm text-green-600 dark:text-green-400"
                >
                  <Plus className="w-4 h-4" /> Add
                </button>
              </div>
              <div className="space-y-2">
                {form[tier].map((row, index) => (
                  <div key={row.id} className="flex flex-col sm:flex-row gap-2">
                    <input
                      className="flex-1 rounded border border-theme-primary bg-theme-secondary px-2 py-1 text-sm text-theme-primary"
                      placeholder="Exercise name"
                      value={row.name}
                      onChange={(e) => updateRow(tier, index, { name: e.target.value })}
                    />
                    <input
                      className="flex-1 rounded border border-theme-primary bg-theme-secondary px-2 py-1 text-sm text-theme-primary"
                      placeholder="Reps or time"
                      value={row.prescription}
                      onChange={(e) => updateRow(tier, index, { prescription: e.target.value })}
                    />
                    <button
                      type="button"
                      onClick={() => removeRow(tier, index)}
                      className="p-2 text-red-600 dark:text-red-400 shrink-0"
                      aria-label="Remove row"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </section>
          ))}

          <div className="flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onSave}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-green-600 text-white font-medium disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setForm(cloneConfig(normalizeWorkoutWheelConfig(DEFAULT_WORKOUT_WHEEL_CONFIG)))}
              className="px-4 py-2 rounded-lg border border-theme-primary text-theme-primary"
            >
              Reset form to defaults
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default WorkoutWheelSettings;
